// FILE: assets/ad-recently-viewed-drawer.js
// Logic for handling asynchronous data fetching, input sorting, and access containment traps.
// Reviewed and modified by Gemini Pro 3.1 Extended 06/07/2026

// GEMINI UPDATE: Added a debounce helper function to prevent rapid-fire storage events from crashing the browser
function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

class RecentlyViewedDrawer extends HTMLElement {
  constructor() {
    super();

    this.storageKey = 'recently-viewed-products';
    this.desktopMedia = window.matchMedia('(min-width: 990px)');
    this.renderedIds = ''; 

    this.addEventListener('keyup', (evt) => evt.key === 'Escape' && this.close());

    this.querySelector('#RecentlyViewedDrawer-Overlay')
      .addEventListener('click', this.close.bind(this));

    // GEMINI UPDATE: Bind the new Clear History button
    const clearBtn = this.querySelector('.drawer__clear');
    if (clearBtn) clearBtn.addEventListener('click', this.clearHistory.bind(this));

    this.setHeaderRecentlyViewedIconAccessibility();

    this.desktopMedia.addEventListener('change', () => {
      this.updateTriggerVisibility();
      if (!this.desktopMedia.matches) this.close();
    });

    // GEMINI UPDATE: Applied debounce to the storage event listener
    const debouncedUpdate = debounce(() => this.updateTriggerVisibility(), 200);
    window.addEventListener('storage', debouncedUpdate);
    document.addEventListener('recently-viewed:updated', () => this.updateTriggerVisibility());

    this.updateTriggerVisibility();
    
    if (this.desktopMedia.matches && this.hasEnoughProducts()) {
      this.renderContents();
    }
  }

  setHeaderRecentlyViewedIconAccessibility() {
    this.trigger = document.querySelector('#recently-viewed-icon-bubble');
    if (!this.trigger) return;

    this.trigger.setAttribute('role', 'button');
    this.trigger.setAttribute('aria-haspopup', 'dialog');
    this.trigger.setAttribute('aria-expanded', 'false');

    this.trigger.addEventListener('click', (event) => {
      event.preventDefault();
      if (!this.desktopMedia.matches || !this.hasEnoughProducts()) return;
      this.open(this.trigger);
    });

    this.trigger.addEventListener('keydown', (event) => {
      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        if (!this.desktopMedia.matches || !this.hasEnoughProducts()) return;
        this.open(this.trigger);
      }
    });
  }

  open(triggeredBy) {
    if (!this.desktopMedia.matches || !this.hasEnoughProducts()) return;

    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (this.trigger) this.trigger.setAttribute('aria-expanded', 'true');

    this.renderContents();

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('RecentlyViewedDrawer');

        const focusElement =
          this.querySelector('.drawer__inner') ||
          this.querySelector('.drawer__close');

        if (typeof trapFocus === 'function') {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }
    document.body.classList.remove('overflow-hidden');
    if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
  }

  // GEMINI UPDATE: Added clearHistory method to wipe localStorage and reset the UI
  clearHistory() {
    localStorage.removeItem(this.storageKey);
    this.renderedIds = '';
    document.dispatchEvent(new CustomEvent('recently-viewed:updated'));
    this.renderContents(); // Re-render triggers empty state logic
  }

  getRecentlyViewedProducts() {
    try {
      const items = JSON.parse(localStorage.getItem(this.storageKey));
      return Array.isArray(items) ? items : [];
    } catch (error) {
      return [];
    }
  }

  hasEnoughProducts() {
    return this.getRecentlyViewedProducts().length >= 2;
  }

  updateTriggerVisibility() {
    const shouldShow = this.desktopMedia.matches && this.hasEnoughProducts();
    document.documentElement.classList.toggle('recently-viewed-icon-ready', shouldShow);
    if (!shouldShow) this.close();
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  async renderContents() {
    const products = this.getRecentlyViewedProducts();
    const itemsContainer = this.querySelector('[data-recently-viewed-items]');
    const drawerItems = this.querySelector('recently-viewed-drawer-items');

    if (!itemsContainer || !drawerItems) return;

    // Handles fallback to empty state if user clears history
    if (products.length < 2) {
      this.classList.add('is-empty');
      drawerItems.classList.add('is-empty');
      itemsContainer.innerHTML = '';
      return;
    }

    const currentIds = products.map((p) => p.id).join(',');

    if (this.renderedIds === currentIds && itemsContainer.innerHTML.trim() !== '') {
      this.classList.remove('is-empty');
      drawerItems.classList.remove('is-empty');
      return;
    }

    try {
      // GEMINI UPDATE: Appended .slice(0, 10) to prevent HTTP 414 URI Too Long errors
      const ids = products
        .map((p) => String(p.id))
        .filter(Boolean)
        .filter((id) => /^\d+$/.test(id))
        .slice(0, 10); 
      
      if (ids.length === 0) return;

      const query = ids.map((id) => `id:${id}`).join(' OR ');
      
      const rootUrl = window.Shopify?.routes?.root || '/';
      const searchUrl = `${rootUrl}search?section_id=ad-recently-viewed&type=product&q=${encodeURIComponent(query)}`;

      const response = await fetch(searchUrl);
      if (!response.ok) throw new Error('Network response evaluation was not ok');
      
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      const renderedGrid = html.querySelector('[data-recently-viewed-rendered-grid]');

      if (renderedGrid && renderedGrid.innerHTML.trim() !== '') {
        
        const htmlItems = Array.from(renderedGrid.querySelectorAll('.recently-viewed-drawer__grid-item'));
        
        htmlItems.sort((a, b) => {
          const idA = a.getAttribute('data-product-id');
          const idB = b.getAttribute('data-product-id');
          return ids.indexOf(idA) - ids.indexOf(idB);
        });

        renderedGrid.innerHTML = '';
        htmlItems.forEach(item => renderedGrid.appendChild(item));

        itemsContainer.innerHTML = renderedGrid.innerHTML;
        this.classList.remove('is-empty');
        drawerItems.classList.remove('is-empty');
        
        this.renderedIds = currentIds; 
      }
    } catch (error) {
      console.error('Failed to load recently viewed products:', error);
    }
  }
}

customElements.define('recently-viewed-drawer', RecentlyViewedDrawer);

class RecentlyViewedDrawerItems extends HTMLElement {}
customElements.define('recently-viewed-drawer-items', RecentlyViewedDrawerItems);
